import React, { createContext, useContext, useState, useEffect, useRef, useMemo, ReactNode } from 'react';
import { getNodes, getNodeCameras } from '../services/api.service';
import { useAuth } from './AuthContext';
import { readStoredScope, storeScope } from '../constants/scope';

export interface NodeInfo {
  nodeId: string;
  name: string;
  status: 'online' | 'offline';
  tunnelActive: boolean;
  lastSeen: number;
  metrics: any;
  version: string;
  capabilities: string[];
  activeSessions: number;
}

interface NodeContextType {
  nodes: NodeInfo[];
  selectedNode: NodeInfo | null;
  /**
   * The SCOPE, independent of whether the node list has arrived: `null` means All Sites,
   * a string means that site.
   *
   * `selectedNode` is the resolved object and is null in two very different situations — All
   * Sites, and "a site is selected but we haven't fetched the list yet". Anything deciding
   * which scope the app is in (the sidebar's tree, the URL sync) must use this instead, or a
   * cold load flashes the All Sites menu before settling on the stored site.
   */
  selectedNodeId: string | null;
  selectNode: (nodeId: string | null) => void;
  loading: boolean;
  /** True while switching to a node and waiting for its data channel (tunnel API) to respond */
  nodeConnecting: boolean;
  dismissConnecting: () => void;
  refreshNodes: () => Promise<void>;
}

const NodeContext = createContext<NodeContextType>({
  nodes: [],
  selectedNode: null,
  selectedNodeId: null,
  selectNode: () => {},
  loading: true,
  nodeConnecting: false,
  dismissConnecting: () => {},
  refreshNodes: async () => {},
});

export function NodeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [nodes, setNodes] = useState<NodeInfo[]>([]);
  // No stored site = All Sites, which is deliberately what a fresh browser (and a fresh login)
  // starts on: the estate view is the only scope that is true before anyone has chosen one.
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(() => {
    // No stored site = All Sites; ScopeSync may then substitute the user's favourite.
    return readStoredScope();
  });
  const [loading, setLoading] = useState(true);
  const [nodeConnecting, setNodeConnecting] = useState(false);
  const connectingAbortRef = useRef<AbortController | null>(null);
  // Track whether the node switch was an explicit user action (not page reload)
  const explicitSwitchRef = useRef(false);

  const fetchNodes = async () => {
    try {
      const data = await getNodes();
      setNodes(data.nodes || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't poll for nodes if user isn't logged in
    if (!user) {
      setNodes([]);
      setLoading(false);
      return;
    }

    let inFlight = false;
    const safeFetch = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try { await fetchNodes(); } finally { inFlight = false; }
    };

    safeFetch();
    const interval = setInterval(safeFetch, 10_000);

    // Pause polling when tab is hidden, resume immediately when visible
    const onVisibility = () => {
      if (!document.hidden) safeFetch();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  // Keep selectedNode reference stable – only update when nodeId changes or node
  // properties actually differ, so downstream useEffects don't re-fire on every poll.
  const matchedNode = nodes.find(n => n.nodeId === selectedNodeId) || null;
  const selectedNodeRef = useRef<NodeInfo | null>(null);

  const selectedNode = useMemo(() => {
    const prev = selectedNodeRef.current;
    if (!matchedNode) { selectedNodeRef.current = null; return null; }
    if (prev && prev.nodeId === matchedNode.nodeId && prev.status === matchedNode.status && prev.name === matchedNode.name) {
      return prev; // same reference, no re-render cascade
    }
    selectedNodeRef.current = matchedNode;
    return matchedNode;
  }, [matchedNode?.nodeId, matchedNode?.status, matchedNode?.name]);

  // Read the current node list from inside the connect effect without making it a dependency —
  // it is refetched every 10s and the effect must only run on an actual node switch.
  const nodesRef = useRef<NodeInfo[]>(nodes);
  nodesRef.current = nodes;

  // When a node is EXPLICITLY switched by the user (not on page reload),
  // show a brief "Connecting…" overlay while we verify the tunnel is reachable.
  // Max 5s, then dismiss automatically so the UI is never permanently stuck.
  useEffect(() => {
    connectingAbortRef.current?.abort();
    connectingAbortRef.current = null;

    if (!selectedNodeId || !explicitSwitchRef.current) {
      setNodeConnecting(false);
      explicitSwitchRef.current = false;
      return;
    }
    explicitSwitchRef.current = false;

    // Already known to be down — probing it just spends 5s on a "Connecting…" overlay before
    // the offline notice appears anyway. Go straight to the notice.
    if (nodesRef.current.find(n => n.nodeId === selectedNodeId)?.status !== 'online') {
      setNodeConnecting(false);
      return;
    }

    const abort = new AbortController();
    connectingAbortRef.current = abort;
    setNodeConnecting(true);

    // Hard timeout — never block UI longer than 5 seconds
    const timeout = setTimeout(() => {
      if (!abort.signal.aborted) setNodeConnecting(false);
    }, 5000);

    const probe = async () => {
      for (let i = 0; i < 4; i++) {
        if (abort.signal.aborted) return;
        try {
          await getNodeCameras(selectedNodeId);
          if (!abort.signal.aborted) setNodeConnecting(false);
          clearTimeout(timeout);
          return;
        } catch {
          if (abort.signal.aborted) return;
          await new Promise(r => setTimeout(r, 1000));
        }
      }
      // Give up — dismiss overlay
      if (!abort.signal.aborted) setNodeConnecting(false);
      clearTimeout(timeout);
    };

    probe();

    return () => { abort.abort(); clearTimeout(timeout); };
  }, [selectedNodeId]);

  const dismissConnecting = () => {
    connectingAbortRef.current?.abort();
    setNodeConnecting(false);
  };

  const selectNode = (nodeId: string | null) => {
    explicitSwitchRef.current = !!nodeId;
    setSelectedNodeId(nodeId);
    // Recorded either way, All Sites included: the stored value is "what this session
    // chose", and an explicit All Sites must outlast a reload rather than reading as
    // "nothing chosen" and being replaced by the favourite site.
    storeScope(nodeId);
  };

  return (
    <NodeContext.Provider value={{ nodes, selectedNode, selectedNodeId, selectNode, loading, nodeConnecting, dismissConnecting, refreshNodes: fetchNodes }}>
      {children}
    </NodeContext.Provider>
  );
}

export function useNode() {
  return useContext(NodeContext);
}
